import { ApiKey } from '@pubstack/common/src/site'
import { Button } from '@pubstack/ui/components/Button'
import { GlobalModalWrapper } from '@pubstack/ui/components/GlobalModalWrapper'
import { Input } from '@pubstack/ui/components/Input'
import { Actions, Body, Title } from '@pubstack/ui/components/ModalLayout'
import Table, { Column, handleTableSearchAndSort, onColumnSort, SortableColumn, TableColumns } from '@pubstack/ui/components/table/Table'
import { TableCell } from '@pubstack/ui/components/table/TableCell'
import { TableRow } from '@pubstack/ui/components/table/TableRow'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useApiKeys, useCreateApiKeys } from '~/api/app/api-keys.api'
import { useGlobalModal } from '~/components/layout/GlobalModal'

const NewKeyModal: FunctionComponent = () => {
  const modal = useGlobalModal()
  const { control, reset, handleSubmit } = useForm<Pick<ApiKey, 'name'>>({
    defaultValues: {
      name: '',
    },
  })
  const name = useWatch({ control, name: 'name' })
  const { mutateAsync: createKey } = useCreateApiKeys({
    errorToast: 'Error on creating API Key',
    successToast: (
      <>
        <strong>{name}</strong> was successfully created
      </>
    ),
  })

  const close = () => {
    reset()
    modal.close()
  }

  const onSubmit = async (key: Pick<ApiKey, 'name'>) => {
    if (key.name) {
      const created = await createKey({ name: key.name }).then(
        () => true,
        () => false
      )
      // on failure the mutation already showed the error toast: keep the form open
      if (!created) return
    }
    close()
  }

  return (
    <GlobalModalWrapper>
      <Title>Add API key</Title>

      <Body>
        <Input name={'name'} rules={{ required: true }} labelIsPlaceholder control={control} label={'API key name'} />
      </Body>

      <Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-detail-api-keys-creation-cancel'}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} trackingLabel={'common-admin-scopes-detail-api-keys-creation-confirm'}>
          Create key
        </Button>
      </Actions>
    </GlobalModalWrapper>
  )
}

const ADMIN_KEYS_COLUMNS: (Column | SortableColumn<ApiKey>)[] = [
  {
    name: 'Name',
    isSortable: true,
    order: 'ascending',
    attributeSort: 'name',
  },
  {
    name: 'Key',
    isSortable: true,
    order: 'none',
    attributeSort: 'id',
  },
]

export const LegacyApiKeysTab: FunctionComponent = () => {
  const { data: apiKeys, isFetching } = useApiKeys()

  const { control } = useForm<Pick<ApiKey, 'name'>>({
    defaultValues: {
      name: '',
    },
  })
  const modal = useGlobalModal()
  const keySearch = useWatch({ control, name: 'name' })

  const [columns, setColumns] = useState<TableColumns<ApiKey>>(ADMIN_KEYS_COLUMNS)

  const displayedKeys = handleTableSearchAndSort(columns, apiKeys || [], keySearch, ['name'])

  const createNewApiKeyButton = (
    <Button onClick={() => modal.open(NewKeyModal, {})} trackingLabel={'common-admin-scopes-detail-api-keys-new'}>
      New API key
    </Button>
  )
  return (
    <div className="flex flex-col gap-3 pb-2.5">
      <div className="flex items-center gap-5">
        <Input iconLeft={'search'} name={'name'} control={control} label={'Search'} labelIsPlaceholder />
        {createNewApiKeyButton}
      </div>
      <Table
        columns={columns}
        headersTop={0}
        isLoading={isFetching}
        onClickHeading={(column) => {
          onColumnSort(columns, column, setColumns)
        }}
      >
        {displayedKeys.length ? (
          displayedKeys.map((apiKey) => (
            <TableRow key={apiKey.id}>
              <TableCell>{apiKey.name}</TableCell>
              <TableCell>{apiKey.id}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div>
                  <h2>No API key</h2>
                  <p>You do not have any API keys yet.</p>
                </div>
                {createNewApiKeyButton}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </div>
  )
}
